import filepath from './filepath'
import { parse } from 'fast-xml-parser'
import { VFS, FileInfo, MountPoint } from './types'

export class MountFilter implements VFS {
  private vfs: VFS
  private allowedContentTypes: string[]

  constructor (vfs: VFS, allowedContentTypes: string[]) {
    this.vfs = vfs
    this.allowedContentTypes = allowedContentTypes
  }

  async ReadDir (path: string): Promise<FileInfo[]> {
    const files: FileInfo[] = await this.vfs.ReadDir(path)
    if (this.allowedContentTypes.length === 0) {
      return files
    }

    return files.filter((f: FileInfo) => {
      return f.IsDir || this.allowedContentTypes.includes(f.ContentType)
    })
  }

  async ResolveFilename (path: string): Promise<string> {
    const f = await this.vfs.Stat(path)
    const filename = this.vfs.ResolveFilename(path)
    if (this.allowedContentTypes.length === 0) {
      return filename
    }
    if (this.allowedContentTypes.includes(f.ContentType)) {
      return filename
    }
    throw new Error(`ResolveFilename(): file ${path} is not allowed or doesn't exist`)
  }

  async Stat (path: string): Promise<FileInfo> {
    // console.log('MountFilter.Stat', path)
    const f = await this.vfs.Stat(path)
    if (this.allowedContentTypes.length === 0) {
      return f
    }
    if (f.IsDir || this.allowedContentTypes.includes(f.ContentType)) {
      return f
    }
    throw new Error(`Stat(): file ${path} is not allowed or doesn't exist`)
  }
}

class FilterVFS implements VFS {
  private vfs: VFS
  constructor (vfs: VFS, public filterKey: string, public filterValue: string) {
    this.vfs = vfs
  }

  async ReadDir (path: string): Promise<FileInfo[]> {
    const files = await this.vfs.ReadDir(path)
    return files.filter((f: FileInfo) => {
      return f.XAttr && f.XAttr[this.filterKey] === this.filterValue
    })
  }

  ResolveFilename (path: string): Promise<string> {
    return this.vfs.ResolveFilename(path)
  }

  async Stat (path: string): Promise<FileInfo> {
    const file = await this.vfs.Stat(path)
    return file
  }
}

export class GroupByXAttr implements VFS {
  private mounts: Promise<{ [path: string]: VFS}>

  constructor (mounts: MountPoint[], public XAttrProp: string) {
    this.mounts = mounts.reduce(async (acc, curr) => {
      const accumulator = await acc
      if (curr.path === '/') {
        const files: FileInfo[] = await curr.vfs.ReadDir('/')
        const xAttrValues = files.reduce((acc: Set<string>, curr) => {
          if (curr.XAttr && curr.XAttr[XAttrProp]) {
            acc.add(curr.XAttr[XAttrProp])
          }
          return acc
        }, new Set<string>())
        Array.from(xAttrValues).forEach((name: string) => {
          accumulator[`/${encodeURIComponent(name)}`] = new FilterVFS(curr.vfs, this.XAttrProp, name)
        })
      } else {
        accumulator[`/${curr.path}`] = curr.vfs
      }
      return accumulator
    }, new Promise<{[path: string]: VFS}>((resolve) => resolve({})))
  }

  private childUrl (path: string): string {
    const pathParts = path.match(/[^/]+/g) || []
    if (pathParts.length === 0) {
      throw new Error('unexpected')
    }
    const topLevel = pathParts[0]
    return path.slice(path.indexOf(topLevel) + topLevel.length) || '/'
  }

  async ReadDir (path: string): Promise<FileInfo[]> {
    const mounts = await this.mounts
    // console.log('GroupByXAttr.ReadDir', path)
    if (path === '/') {
      const files = Promise.all(Object.entries(mounts).map(async (v: [string, VFS]) => {
        const [name, vfs] = v
        return {
          Name: decodeURIComponent(name.replace('/', '')),
          ContentType: '',
          IsDir: (await vfs.Stat(path)).IsDir
        }
      }))
      return files
    }

    const entry = Object.entries(mounts).find((m: [string, VFS]) => {
      const [p, vfs] = m
      return decodeURIComponent(path).startsWith(decodeURIComponent(p))
    })
    if (!entry) {
      throw new Error('GroupByXAttr.ReadDir unable to resolve virtual fs')
    }

    const [, vfs] = entry
    path = this.childUrl(path)
    const files = vfs.ReadDir(path)
    return files
  }

  async ResolveFilename (path: string): Promise<string> {
    // console.log('GroupByXAttr.ResolveFilename', path)
    const mounts = await this.mounts

    const entry = Object.entries(mounts).find((m: [string, VFS]) => {
      const [p, vfs] = m
      return decodeURIComponent(path).startsWith(decodeURIComponent(p))
    })
    if (!entry) {
      throw new Error('GroupByXAttr.ResolveFilename unable to resolve virtual fs')
    }

    const [, vfs] = entry
    path = this.childUrl(path)
    return vfs.ResolveFilename(path)
  }

  async Stat (path: string): Promise<FileInfo> {
    // console.log('GroupByXAttr.Stat', path)
    const pathParts = path.match(/[^/]+/g) || []
    if (pathParts.length === 0) {
      return {
        Name: '',
        IsDir: true,
        ContentType: ''
      }
    }
    const mounts = await this.mounts
    const entry = Object.entries(mounts).find((m: [string, VFS]) => {
      const [p, vfs] = m
      return decodeURIComponent(path).startsWith(decodeURIComponent(p))
    })
    if (!entry) {
      throw new Error('GroupByXAttr.Stat unable to resolve virtual fs')
    }
    const [, vfs] = entry
    path = this.childUrl(path)
    const f = await vfs.Stat(path)
    return f
  }
}

export class Mount implements VFS {
  private vfsMap: { [path: string]: VFS}

  constructor (public mountPoints: MountPoint[]) {
    this.vfsMap = mountPoints.reduce<{ [path: string]: VFS}>((acc: { [path: string]: VFS}, curr: MountPoint) => {
      acc[`/${curr.path}`] = curr.vfs
      return acc
    }, {})
  }

  private resolveVFS (path: string): [string, VFS] {
    const entry = Object.entries(this.vfsMap).find((m: [string, VFS]) => {
      const [p, vfs] = m
      return decodeURIComponent(path).startsWith(decodeURIComponent(p))
    })

    if (!entry) {
      throw new Error('unable to resolve virtual fs')
    }

    return entry
  }

  private childUrl (path: string): string {
    const pathParts = path.match(/[^/]+/g) || []
    if (pathParts.length === 0) {
      throw new Error('unexpected')
    }
    const topLevel = pathParts[0]
    return path.slice(path.indexOf(topLevel) + topLevel.length) || '/'
  }

  async ReadDir (path: string): Promise<FileInfo[]> {
    // console.log('Mount.ReadDir', path)
    if (path === '/') {
      const files = Object.entries(this.vfsMap).map(async (mountPoint: [string, VFS]) => {
        const [p, vfs] = mountPoint

        const file = await vfs.Stat(path)
        return {
          Name: decodeURIComponent(p).replace('/', ''),
          ContentType: file.ContentType,
          IsDir: file.IsDir
        }
      })

      return await Promise.all(files)
    }

    const entry = this.resolveVFS(path)
    path = this.childUrl(path)
    const [, vfs] = entry
    return vfs.ReadDir(path)
  }

  ResolveFilename (path: string): Promise<string> {
    // console.log('Mount.ResolveFilename', path)
    if (path === '/') {
      throw new Error('unable to resolve filename for directory')
    }

    const entry = this.resolveVFS(path)
    path = this.childUrl(path)
    const [, vfs] = entry
    return vfs.ResolveFilename(path)
  }

  async Stat (path: string): Promise<FileInfo> {
    // console.log('Mount.Stat', path)

    if (path === '/') {
      return {
        Name: '',
        ContentType: '',
        IsDir: true
      }
    }

    const entry = this.resolveVFS(path)
    path = this.childUrl(path)
    const [, vfs] = entry
    return vfs.Stat(path)
  }
}

export class PrefixVFS implements VFS {
  private vfs: VFS
  private prefix: string

  constructor (vfs: VFS, prefix: string) {
    this.vfs = vfs
    this.prefix = prefix
  }

  ReadDir (path: string): Promise<FileInfo[]> {
    // console.log('PrefixVFS.ReadDir', path)
    return this.vfs.ReadDir(path === '/' ? this.prefix : filepath.join(this.prefix, path))
  }

  ResolveFilename (path: string): Promise<string> {
    // console.log('PrefixVFS.ResolveFilename', p)
    return this.vfs.ResolveFilename(path === '/' ? this.prefix : filepath.join(this.prefix, path))
  }

  Stat (path: string): Promise<FileInfo> {
    // console.log('PrefixVFS.Stat', p)
    return this.vfs.Stat(path === '/' ? this.prefix : filepath.join(this.prefix, path))
  }
}

export class JohnsManvilleVFS implements VFS {
  private cardId = ''

  constructor (public endpointEHuman: string, public server: string) {
    // observable.attach(() => {
    console.log('')
    // })
  }

  update (code: string): void {
    this.cardId = code
    this.cardId = '000000000411098' // TODO: remove
  }

  ReadDir (path: string): Promise<FileInfo[]> {
    throw new Error(`${path} is not directory`)
  }

  getPersonalNumber (): Promise<string> {
    return new Promise((resolve) => {
      resolve('000000000411098')
    })
  }

  async fetch (input: RequestInfo, init?: RequestInit): Promise<string | undefined> {
    return fetch(input, init).then((res: Response) => {
      if (!window.caches) {
        return res.text()
      }
      return caches.open('johns-manville-ehuman').then((c: Cache) => {
        return c.put(input, res.clone()).then(() => {
          return res.text()
        })
      })
    }).catch(async () => {
      console.warn(`Error while fetching ${input}. Trying use cache`)
      const c = await caches.open('johns-manville-ehuman')
      const res = await c.match(input)
      return res?.text()
    })
  }

  async getKioskAthParams (): Promise<KioskAuthParams> {
    const body = `<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/">
    <Body>
        <GetKioskAuthParams xmlns="http://www.hour.sk/schemas/eHuman"/>
    </Body>
</Envelope>`

    const headers: HeadersInit = new Headers()
    headers.set('Content-Type', 'text/xml; charset="UTF-8"')

    const opts: RequestInit = {
      method: 'POST',
      headers,
      body: body
    }

    const res = await this.fetch(this.endpointEHuman, opts)
    if (!res) {
      throw new Error('bad request')
    }
    const root: any = parse(res, {
      ignoreAttributes: false,
      ignoreNameSpace: true
    })
    const { idType, passwordRequired } = root.Envelope.Body.GetKioskAuthParamsResponse.GetKioskAuthParamsResult
    return {
      idType,
      passwordRequired
    }
  }

  async getCompanyList (): Promise<CompanyInfo> {
    const body = `<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/">
    <Body>
        <GetCompanyList xmlns="http://www.hour.sk/schemas/eHuman"/>
    </Body>
</Envelope>`

    const headers: HeadersInit = new Headers()
    headers.set('Content-Type', 'text/xml; charset="UTF-8"')

    const opts: RequestInit = {
      method: 'POST',
      headers,
      body: body
    }

    const res = await this.fetch(this.endpointEHuman, opts)
    if (!res) {
      throw new Error('bad request')
    }
    const root: any = parse(res, {
      ignoreAttributes: false,
      ignoreNameSpace: true
    })

    const { id, name } = root.Envelope.Body.GetCompanyListResponse.GetCompanyListResult.CompanyInfo
    return {
      id,
      name
    }
  }

  async loginKiosk (login: Login): Promise<LoginKioskResult> {
    const body = `<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/">
    <Body>
        <LoginKiosk xmlns="http://www.hour.sk/schemas/eHuman">
            <companyId>${login.companyId}</companyId>
            <userId>${login.username}</userId>
            <password>${login.password}</password>
        </LoginKiosk>
    </Body>
</Envelope>`

    const headers: HeadersInit = new Headers()
    headers.set('Content-Type', 'text/xml; charset="UTF-8"')

    const opts: RequestInit = {
      method: 'POST',
      headers,
      body: body
    }

    const res = await this.fetch(this.endpointEHuman, opts)
    if (!res) {
      throw new Error('bad request')
    }
    const root: any = parse(res, {
      ignoreAttributes: false,
      ignoreNameSpace: true
    })
    const { code, message } = root.Envelope.Body.LoginKioskResponse.LoginKioskResult
    return { code, message }
  }

  async switchToKioskUiRequest (): Promise<SwitchUIModeResult> {
    const body = `<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/">
    <Body>
        <SwitchUIMode xmlns="http://www.hour.sk/schemas/eHuman">
            <mode>UI_KIOSK</mode>
        </SwitchUIMode>
    </Body>
</Envelope>`

    const headers: HeadersInit = new Headers()
    headers.set('Content-Type', 'text/xml; charset="UTF-8"')

    const opts: RequestInit = {
      method: 'POST',
      headers,
      body: body
    }

    const res = await this.fetch(this.endpointEHuman, opts)
    if (!res) {
      throw new Error('bad request')
    }
    const root: any = parse(res, {
      ignoreAttributes: false,
      ignoreNameSpace: true
    })
    return root.Envelope.Body.SwitchUIModeResponse.SwitchUIModeResult
  }

  async logoutKiosk (): Promise<LogoutResult> {
    const body = `<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/">
  <Body>
    <Logout xmlns="http://www.hour.sk/schemas/eHuman"/>
  </Body>
</Envelope>`

    const headers: HeadersInit = new Headers()
    headers.set('Content-Type', 'text/xml; charset="UTF-8"')

    const opts: RequestInit = {
      method: 'POST',
      headers,
      body: body
    }

    const res = await this.fetch(this.endpointEHuman, opts)
    if (!res) {
      throw new Error('bad request')
    }
    const root: any = parse(res, {
      ignoreAttributes: false,
      ignoreNameSpace: true
    })
    const { code, message } = root.Envelope.Body.LogoutResponse.LogoutResult
    return { code, message }
  }

  async ResolveFilename (path: string): Promise<string> {
    if (!this.cardId) {
      throw new Error('Login is required!')
    }
    await this.logoutKiosk()
    const companyInfo: CompanyInfo = await this.getCompanyList()
    await this.getKioskAthParams()
    await this.loginKiosk({
      companyId: companyInfo.id,
      username: this.cardId,
      password: ''
    })
    await this.switchToKioskUiRequest()

    this.cardId = ''
    return new Promise<string>((resolve) => {
      resolve(new URL('/', this.endpointEHuman).href)
    })
  }

  Stat (path: string): Promise<FileInfo> {
    return new Promise<FileInfo>((resolve) => {
      resolve({
        Name: '',
        ContentType: '',
        IsDir: false
      })
    })
  }
}

export class WebVFS implements VFS {
  private url: string
  constructor (url: string) {
    this.url = url
  }

  ReadDir (path: string): Promise<FileInfo[]> {
    throw new Error('Not supported!')
  }

  ResolveFilename (path: string): Promise<string> {
    return new Promise<string>((resolve) => resolve(this.url))
  }

  Stat (path: string): Promise<FileInfo> {
    return new Promise<FileInfo>((resolve) => resolve({
      ContentType: 'text/html',
      IsDir: false,
      Name: ''
    }))
  }
}

interface Login {
  companyId: string;
  username: string;
  password: string;
}

interface CompanyInfo {
  id: string;
  name: string;
}

interface KioskAuthParams {
  idType: string;
  passwordRequired: boolean;
}

interface LoginKioskResult {
  code: 'LOGIN_SUCCESSFUL' | 'INVALID_COMPANY' | 'LOGIN_FAILED' | 'USER_INACTIVE' | string;
  message: string;
}

interface LogoutResult {
  code: 'LOGOUT_SUCCESSFUL' | 'NO_USER_LOGGED_IN' | string;
  message: string;
}

type SwitchUIModeResult = 'UI_NORMAL' | 'UI_TOUCH' | 'UI_KIOSK' | string
